import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { TableCell, TableRow } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { CustomerItemDto } from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { customerStatusUtil } from '@/views/customers/status/customerStatus.util.ts';
import { CustomerStatusChip } from '@/views/customers/status/CustomerStatusChip.comp.tsx';
import { processCreateAddCustomerBlockedRowStyles } from '@/views/processesCreate/components/ProcessCreateAddCustomerTableRow.styles.ts';

type Props = {
  onCustomAction?: (row: CustomerItemDto) => void;
  /**
   * The customer is chosen as the customer of a process (creation or change of the customer): only an
   * ACTIVE customer can become one, so a Prospekt, an Odrzucony klient and an inactive customer are
   * greyed out and cannot be chosen. Other pickers (e.g. the customer of a contact) accept every status.
   */
  onlyProcessCustomers?: boolean;
  row: CustomerItemDto;
};

/** A customer to choose in the customer picker, with its status. */
export const ProcessCreateAddCustomerTableRow = ({
  onCustomAction,
  onlyProcessCustomers = false,
  row,
}: Props) => {
  const { t } = useTranslationWithPrefix('views.customers.table.body');
  const { t: tPicker } = useTranslationWithPrefix('views.customers.status.processPicker');
  const isSelectable = !onlyProcessCustomers || customerStatusUtil.canBeProcessCustomer(row.status);
  const blockedReason = tPicker(customerStatusUtil.processCustomerBlockedReason(row.status));

  const handleSelectCustomer = () => {
    onCustomAction?.(row);
  };

  return (
    <TableRow
      aria-disabled={!isSelectable || undefined}
      hover={isSelectable}
      sx={isSelectable ? undefined : processCreateAddCustomerBlockedRowStyles}
    >
      <TableCell>{row.name}</TableCell>
      <TableCell>{row.nip}</TableCell>
      <TableCell>{row.address}</TableCell>
      <TableCell>{t(`typeOption.${row.type}`)}</TableCell>
      <TableCell>{t(`flagOption.${row.flag}`)}</TableCell>
      <TableCell>
        <CustomerStatusChip status={row.status} />
      </TableCell>
      <TableCell>
        {isSelectable ? (
          <IconButton aria-label={t('selectCustomer')} onClick={handleSelectCustomer}>
            <CheckCircleOutlinedIcon htmlColor={themeColors.green} />
          </IconButton>
        ) : (
          <Tooltip title={blockedReason}>
            <span>
              <IconButton
                aria-label={t('selectCustomerBlocked', { reason: blockedReason })}
                disabled
              >
                <CheckCircleOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};
